import styled from '@emotion/styled'
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import { ReactNode } from 'react'

import { Color, theme } from '../../../constants'
import { Icon, IconName } from '../../Icon'
import { ItemContent } from '../ItemContent'
import { SelectedCheckmark } from '../SelectedCheckmark'
import { itemStyles, panelAnimation, panelStyles } from '../styles'
import { MenuItem } from '../types'
import { isNotHidden } from '../utils'

const Content = styled(RadixDropdownMenu.Content)({
  zIndex: theme.zIndex.popover,
  ...panelStyles,
  ...panelAnimation,
})
const SubContent = styled(RadixDropdownMenu.SubContent)({
  ...panelStyles,
  ...panelAnimation,
  zIndex: theme.zIndex.popover,
})

const SubMenuWrapper = styled(RadixDropdownMenu.Sub)({
  zIndex: theme.zIndex.popover,
})

const Item = styled(RadixDropdownMenu.Item)(itemStyles)
const CheckboxItem = styled(RadixDropdownMenu.CheckboxItem)(itemStyles)
const RadioGroup = styled(RadixDropdownMenu.RadioGroup)({})
const RadioItem = styled(RadixDropdownMenu.RadioItem)(itemStyles)
const SubTrigger = styled(RadixDropdownMenu.SubTrigger)({
  ...itemStyles,
  '&[data-state="open"]': {
    background: Color.LIGHTEST_BLUE,
    color: Color.CHARCOAL,
  },
})

const SubMenu = (props: {
  item: { icon: IconName; label: string }
  children: ReactNode
}) => {
  return (
    <SubMenuWrapper>
      <SubTrigger>
        <ItemContent
          icon={props.item.icon}
          rightSlot={<Icon name="ChevronRightIcon" size="sm" />}
        >
          {props.item.label}
        </ItemContent>
      </SubTrigger>
      <RadixDropdownMenu.Portal>
        <SubContent sideOffset={4} alignOffset={-5}>
          {props.children}
        </SubContent>
      </RadixDropdownMenu.Portal>
    </SubMenuWrapper>
  )
}

type DropdownMenuProps = {
  trigger: ReactNode
  items: MenuItem[]
  disabled?: boolean
  side?: 'left' | 'right' | 'bottom'
  align?: 'start' | 'end' | 'center'
}

export const DropdownMenu = (props: DropdownMenuProps) => {
  return (
    <RadixDropdownMenu.Root>
      <RadixDropdownMenu.Trigger asChild disabled={props.disabled}>
        {props.trigger}
      </RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Portal>
        <Content sideOffset={5} side={props.side} align={props.align}>
          {props.items
            .filter((item) => !item.hidden)
            .map((item, index) => {
              if (item.type === 'action') {
                return (
                  <Item key={index} onSelect={item.onClick}>
                    <ItemContent icon={item.icon}>{item.label}</ItemContent>
                  </Item>
                )
              }

              if (item.type === 'sub-action') {
                return (
                  <SubMenu key={index} item={item}>
                    {item.subMenu
                      .filter(isNotHidden)
                      .map((subItem, subIndex) => (
                        <Item key={subIndex} onSelect={subItem.onClick}>
                          <ItemContent icon={subItem.icon}>
                            {subItem.label}
                          </ItemContent>
                        </Item>
                      ))}
                  </SubMenu>
                )
              }

              if (item.type === 'sub-checkbox') {
                return (
                  <SubMenu key={index} item={item}>
                    {item.subMenu
                      .filter(isNotHidden)
                      .map((subItem, subIndex) => {
                        const checked = item.selected.includes(subItem.value)
                        return (
                          <CheckboxItem
                            key={subIndex}
                            checked={checked}
                            onSelect={(event) => event.preventDefault()}
                            onCheckedChange={() =>
                              item.toggleSelected(subItem.value)
                            }
                          >
                            <ItemContent
                              icon={subItem.icon}
                              rightSlot={
                                <SelectedCheckmark
                                  selected={item.selected.includes(
                                    subItem.value,
                                  )}
                                />
                              }
                            >
                              {subItem.label}
                            </ItemContent>
                          </CheckboxItem>
                        )
                      })}
                  </SubMenu>
                )
              }

              return (
                <SubMenu key={index} item={item}>
                  <RadioGroup
                    value={item.selected}
                    onValueChange={item.onSelect}
                  >
                    {item.subMenu
                      .filter(isNotHidden)
                      .map((subItem, subIndex) => (
                        <RadioItem
                          key={subIndex}
                          value={subItem.value}
                          onSelect={(event) => event.preventDefault()}
                        >
                          <ItemContent
                            icon={subItem.icon}
                            rightSlot={
                              <SelectedCheckmark
                                selected={subItem.value === item.selected}
                              />
                            }
                          >
                            {subItem.label}
                          </ItemContent>
                        </RadioItem>
                      ))}
                  </RadioGroup>
                </SubMenu>
              )
            })}
        </Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  )
}
